import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from './base';
import {
  CreateListDTO, ListDTO, ListGuest, CreateListGuestBulkDTO, CreateAdminListDTO,
} from '../../models/list';
import { Response } from '../../models/shared';

const listApi = createApi({
  reducerPath: 'listsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['lists', 'list-guests'],
  endpoints: (build) => ({
    getLists: build.query<ListDTO[], { user_id: string }>({
      query: ({ user_id }) => ({
        url: '/lists',
        method: 'GET',
        params: {
          user_id,
        },
      }),
      providesTags: ['lists'],
      transformResponse: (response: Response<ListDTO[]>) => response.data || [],
    }),
    getListsByEventId: build.query<ListDTO[], { event_id: string }>({
      query: ({ event_id }) => ({
        url: '/lists',
        method: 'GET',
        params: {
          event_id,
        },
      }),
      providesTags: ['lists'],
      transformResponse: (response: Response<ListDTO[]>) => response.data || [],
    }),
    getListById: build.query<ListDTO | null, string>({
      query: (listId) => ({
        url: `/lists/${listId}`,
        method: 'GET',
      }),
      providesTags: ['lists'],
      transformResponse: (response: Response<ListDTO>) => response.data || null,
    }),
    createList: build.mutation<ListDTO, CreateListDTO>({
      query: (list) => ({
        url: '/lists',
        method: 'POST',
        body: list,
      }),
      invalidatesTags: ['lists'],
    }),
    createAdminList: build.mutation<ListDTO, CreateAdminListDTO>({
      query: (list) => ({
        url: '/lists/create-admin',
        method: 'POST',
        body: list,
      }),
      invalidatesTags: ['lists'],
    }),
    getListGuests: build.query<ListGuest[], string>({
      query: (listId) => ({
        url: `/lists/${listId}/list-guests`,
        method: 'GET',
      }),
      providesTags: ['list-guests'],
      transformResponse: (response: Response<ListGuest[]>) => response.data || [],
    }),
    createListGuests: build.mutation<ListGuest, CreateListGuestBulkDTO>({
      query: ({
        list_id, names_text, related_person,
      }) => ({
        url: `/lists/${list_id}/list-guests/bulk`,
        method: 'POST',
        body: {
          names_text,
          related_person,
          status: 'pending',
          list_id,
        },
      }),
      invalidatesTags: ['list-guests'],
    }),
    acceptListGuests: build.mutation<ListGuest[], { list_id: string, list_guest_ids: string[] }>({
      query: ({ list_id, list_guest_ids }) => ({
        url: `/lists/${list_id}/list-guests/accept`,
        method: 'POST',
        body: { list_guest_ids },
      }),
      invalidatesTags: ['list-guests'],
    }),
  }),
});

export const {
  useGetListsQuery,
  useCreateListMutation,
  useCreateAdminListMutation,
  useGetListByIdQuery,
  useGetListsByEventIdQuery,
  useGetListGuestsQuery,
  useCreateListGuestsMutation,
  useAcceptListGuestsMutation,
} = listApi;

export { listApi };
